import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, from, firstValueFrom } from "rxjs";
import { map, shareReplay, timeout } from "rxjs/operators";
import { HybridStorageKeys, HybridStorageService } from "../../services/hybrid-storage.service";
import { RuntimeConfigService } from "../../../services/runtime-config.service";

/** The fields we use from a school object in the CMS /api/schools payload. */
interface CmsSchool {
  SchoolID: number;
  NodeAlias: string | null;
}

/**
 * Resolves a school's public website URL on nhaschools.com from the CMS school list.
 * The URL slug (NodeAlias) does not always match the school's display name (e.g. "Ypsilanti
 * Charter Academies" → "ypsilanti-academies"), so never slugify names — always use the alias
 * the CMS returns. Never hardcode school URLs.
 */
@Injectable({
  providedIn: 'root'
})
export class SchoolWebsiteService {
  // The full payload is ~300 KB and changes rarely — fetch once, keep only the
  // SchoolID → NodeAlias map, and cache it for a day.
  private static readonly CACHE_MINUTES = 24 * 60;

  private static readonly FETCH_TIMEOUT_MS = 15_000;

  // One shared list fetch per session; persisted lookups are served from hybrid storage.
  private aliasBySchoolEID$?: Observable<Record<number, string> | null>;

  constructor(
    private http: HttpClient,
    private hybridStorageService: HybridStorageService,
    private runtimeConfig: RuntimeConfigService
  ) { }

  // Public, unauthenticated endpoint (CORS *). Unversioned internal CMS plumbing, but NHA's
  // production Scholar API depends on the same host, so it won't change silently.
  private get cmsBaseUrl(): string {
    return this.runtimeConfig.currentConfig.cmsBaseUrl;
  }

  /**
   * Emits the school's public website URL (the /en English page; /es serves Spanish), or null
   * when the school isn't in the CMS or the lookup fails — callers hide the link either way.
   * schoolEID is the NHA/SIS integer school ID (School.SchoolEID, not the local Core.Schools Id).
   */
  getSchoolWebsiteUrl(schoolEID: number): Observable<string | null> {
    if (!this.aliasBySchoolEID$) {
      this.aliasBySchoolEID$ = from(this.loadAliasMap()).pipe(shareReplay(1));
    }
    return this.aliasBySchoolEID$.pipe(
      map(aliases => {
        const alias = aliases?.[schoolEID];
        return alias ? `${this.cmsBaseUrl}/schools/${alias}/en` : null;
      })
    );
  }

  private async loadAliasMap(): Promise<Record<number, string> | null> {
    const stored = await this.hybridStorageService.getItemIfFresh<Record<number, string>>(
      HybridStorageKeys.SchoolWebsite_AliasMap, SchoolWebsiteService.CACHE_MINUTES);
    if (stored) {
      return stored;
    }

    try {
      const response = await firstValueFrom(this.http.get<{ Data?: CmsSchool[] } | null>(
        `${this.cmsBaseUrl}/api/schools`)
        .pipe(timeout(SchoolWebsiteService.FETCH_TIMEOUT_MS)));

      const aliases: Record<number, string> = {};
      for (const school of response?.Data ?? []) {
        if (school.SchoolID && school.NodeAlias) {
          aliases[school.SchoolID] = school.NodeAlias;
        }
      }

      // An empty map means the payload shape changed on us — treat it like a failure rather
      // than caching "no school has a website" for a day.
      if (Object.keys(aliases).length === 0) {
        this.aliasBySchoolEID$ = undefined;
        return null;
      }

      await this.hybridStorageService.setItem(HybridStorageKeys.SchoolWebsite_AliasMap, aliases);
      return aliases;
    } catch (error) {
      // Fail soft: hide the links. Not cached, and the in-session share is dropped, so the
      // next main-menu visit retries.
      console.error('Error fetching school list from CMS for website URLs', error);
      this.aliasBySchoolEID$ = undefined;
      return null;
    }
  }
}
